import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import {
    ChevronLeft,
    ChevronRight,
    Search,
    Users as UsersIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import DialogCreateGroup from "./DialogCreateGroup";
import GroupRow from "./GroupRow";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const Groups = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const isAdminPanel = location.pathname.startsWith("/admin");
    const { isAdmin, isSuperAdmin, isSupport } = useAuth();
    const actAsAdmin = (isSuperAdmin || isSupport) && !isAdminPanel;
    const canManage = isAdmin || isSuperAdmin || isSupport;

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [deletingGroupId, setDeletingGroupId] = useState(null);
    const debounceRef = useRef(null);

    const { call: getGroups, response, isLoading, error } = useBluelagoon({
        method: "getGroups",
        resetResponse: false,
    });
    const { call: deleteGroup } = useBluelagoon({ method: "deleteGroup" });

    useEffect(
        () => () => debounceRef.current && clearTimeout(debounceRef.current),
        []
    );

    const fetchGroups = useCallback(() => {
        return getGroups({
            search: debouncedSearch,
            limit: PAGE_SIZE,
            offset: (currentPage - 1) * PAGE_SIZE,
            companyOnly: actAsAdmin || undefined,
        }).catch((err) => console.error("Failed to fetch groups:", err));
    }, [getGroups, debouncedSearch, currentPage, actAsAdmin]);

    useEffect(() => {
        if (canManage) fetchGroups();
    }, [canManage, fetchGroups]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const handleDelete = async (groupId) => {
        setDeletingGroupId(groupId);
        try {
            await deleteGroup({ id: groupId });
            toast.success(t("groups.groupDeleted"));
            fetchGroups();
        } catch (err) {
            console.error("Failed to delete group:", err);
            toast.error(err?.message || t("groups.failedToDeleteGroup"));
        } finally {
            setDeletingGroupId(null);
        }
    };

    if (!canManage) {
        return (
            <div className="p-4 text-center">
                <h2 className="text-xl font-semibold mb-2">
                    {t("groups.accessDenied")}
                </h2>
                <p className="text-gray-600">
                    {t("groups.accessDeniedDescription")}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-red-500 bg-red-50 rounded">
                {t("groups.errorLoadingGroups", { message: error.message })}
            </div>
        );
    }

    const groups = response?.data?.groups || [];
    const total = response?.data?.pagination?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const fromRow = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const toRow = Math.min(currentPage * PAGE_SIZE, total);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="font-semibold text-xl flex items-center gap-2">
                    <UsersIcon className="w-5 h-5" />
                    {t("groups.title")}
                </h2>
                <DialogCreateGroup onCreated={fetchGroups} />
            </div>

            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        type="text"
                        placeholder={t("groups.searchPlaceholder")}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="pl-10"
                    />
                </div>
                {total > 0 && (
                    <div className="text-sm text-gray-600">
                        {t("groups.showing", { from: fromRow, to: toRow, total })}
                    </div>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{t("groups.name")}</TableHead>
                        <TableHead>{t("groups.description")}</TableHead>
                        <TableHead>{t("groups.members")}</TableHead>
                        <TableHead>{t("groups.created")}</TableHead>
                        <TableHead className="w-[200px]">
                            {t("groups.actions")}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading && !response ? (
                        [...Array(3)].map((_, i) => (
                            <TableRow key={`group-loading-${i}`}>
                                {[150, 200, 80, 100, 150].map((w, j) => (
                                    <TableCell key={j}>
                                        <Skeleton
                                            className="h-4"
                                            style={{ width: w }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : groups.length > 0 ? (
                        groups.map((group) => (
                            <GroupRow
                                key={group.id}
                                group={group}
                                actAsAdmin={actAsAdmin}
                                onChange={fetchGroups}
                                deletingGroupId={deletingGroupId}
                                onDelete={handleDelete}
                            />
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                className="text-center py-8 text-gray-500"
                            >
                                {debouncedSearch
                                    ? t("groups.noGroupsMatchSearch")
                                    : t("groups.noGroupsFound")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        {t("groups.pageOf", {
                            current: currentPage,
                            total: totalPages,
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((p) => p - 1)}
                            disabled={currentPage === 1}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            {t("groups.previous")}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((p) => p + 1)}
                            disabled={currentPage >= totalPages}
                        >
                            {t("groups.next")}
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Groups;
